"use client";

import { useState, useEffect, useCallback } from "react";
import { Check, ChevronDown, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/themes/ui/button";
import { ImRadioUnchecked } from "react-icons/im";

type FilterOption = {
  value: string;
  label: string;
  count: number;
  subcategories?: FilterOption[];
  sortorder?: number;
  id?: number;
};

type CategoryLeftFilterProps = {
  filters: { category: FilterOption[] };
  searchTerm: string;
  productCount: number;
  selectedCategoryData: any;
  selectedFilters: { [key: string]: string[] };
  selectedMainCategory: string | null;
  openPaths: string[];
  onOpenPathsChange: (paths: string[]) => void;
  onFilterChange: (type: string, value: string, checked: boolean, openPathUpdate?: string[]) => void;
  onClearAll: () => void;
  setCategorySlug: (slug: string) => void;
  slug?: string;
};

/* ------------------------------------------------------------------ */
/*  Recursive tree node - FIXED                                       */
/* ------------------------------------------------------------------ */
const CategoryTreeNode = ({
  node,
  path,
  depth = 0,
  selected,
  openPaths,
  onPathsChange,
  onToggle,
  setSlug,
}: {
  node: FilterOption;
  path: string[];
  depth?: number;
  selected: string[];
  openPaths: string[];
  onPathsChange: (paths: string[]) => void;
  onToggle: (value: string, checked: boolean, newOpenPaths?: string[]) => void;
  setSlug: (slug: string) => void;
}) => {
  const pathKey = path.join("/");
  const isOpen = openPaths.includes(pathKey);
  const hasChildren = !!node.subcategories?.length;
  const checked = selected.includes(node.value);

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newChecked = !checked;
    const newOpenPaths = path;

    onToggle(node.value, newChecked, newChecked ? newOpenPaths : undefined);
    if (newChecked) setSlug(node.value);
  };

  return (
    <div className={depth > 0 ? "border-l border-gray-200 ml-5" : ""}>
      {/* ----- ROW ----- */}
      <div
        className="flex items-center justify-between py-1.5 px-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={handleToggleClick}
      >
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          {/* Checkbox */}
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${checked ? "border-primary bg-primary" : "border-gray-400 bg-white"
              }`}
          >
            {checked ? (
              <Check className="w-4 h-4 text-white" />
            ) : (
              <div className="border rounded-[4px] bg-white border-solid p-[9px] border-gray-400 h-2 w-2" />
            )}
          </div>

          {/* Label + Count */}
          <span className="text-sm text-gray-800 truncate">
            {node.label}
          </span>
        </div>

        {/* Arrow */}
        {hasChildren && (
          <ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""
              }`}
          />
        )}
      </div>

      {/* ----- CHILDREN (smooth open/close) ----- */}
      <AnimatePresence initial={false}>
        {hasChildren && isOpen && (
          <motion.div
            layout
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="py-1">
              {node.subcategories!
                .sort((a, b) => (a.sortorder ?? 0) - (b.sortorder ?? 0))
                .map((child) => (
                  <CategoryTreeNode
                    key={child.value}
                    node={child}
                    path={[...path, child.value]}
                    depth={depth + 1}
                    selected={selected}
                    openPaths={openPaths}
                    onPathsChange={onPathsChange}
                    onToggle={onToggle}
                    setSlug={setSlug}
                  />
                ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component - FIXED with Scroll to Top                         */
/* ------------------------------------------------------------------ */
export default function CategoryLeftFilter({
  filters,
  searchTerm,
  productCount,
  selectedCategoryData,
  selectedFilters,
  selectedMainCategory,
  openPaths,
  onOpenPathsChange,
  onFilterChange,
  onClearAll,
  setCategorySlug,
  slug,
}: CategoryLeftFilterProps) {
  const [showAll, setShowAll] = useState(false);
  const [initialSlugApplied, setInitialSlugApplied] = useState(false);
  const pathname = usePathname();

  /* ------------------------------------------------------------------ */
  /*  Validate openPaths – keep only paths that really exist            */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (openPaths.length === 0) return;
    const valid = openPaths.filter((p) =>
      p.split("/").every((seg) => findNodeByValue(filters.category, seg))
    );
    if (valid.length !== openPaths.length) onOpenPathsChange(valid);
  }, [filters.category, openPaths, onOpenPathsChange]);

  /* ------------------------------------------------------------------ */
  /*  Auto-select from URL slug                                        */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!slug || initialSlugApplied) return;
    const node = findNodeByValue(filters.category, slug);
    if (node) {
      const pathToNode = findPathToNode(filters.category, slug);
      onFilterChange("category", slug, true, pathToNode);
      setInitialSlugApplied(true);
    }
  }, [slug, filters, onFilterChange, initialSlugApplied]);

  /* ------------------------------------------------------------------ */
  /*  Scroll to top on route/pathname change                           */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  /* ------------------------------------------------------------------ */
  /*  Toggle handler – receives the full open-path from the node        */
  /* ------------------------------------------------------------------ */
  const handleToggle = useCallback(
    (value: string, checked: boolean, newOpenPaths?: string[]) => {
      // Update filter selection (single select)
      onFilterChange("category", value, checked);

      if (checked && newOpenPaths) {
        // Open the full path to this node
        const newPathStr = newOpenPaths.join("/");
        const updatedPaths = [
          ...openPaths.filter((p) => {
            const parts = p.split("/");
            const newParts = newOpenPaths;
            // Keep ancestors and remove siblings at same level
            return parts.length < newParts.length || p === newPathStr;
          }),
          newPathStr,
        ];
        onOpenPathsChange(updatedPaths);
      } else if (!checked) {
        // Close everything under this node
        onOpenPathsChange(
          openPaths.filter((p) => !p.startsWith(value + "/") && p !== value)
        );
      }
    },
    [onFilterChange, onOpenPathsChange, openPaths]
  );

  const allCats = filters.category;
  const displayed = showAll ? allCats : allCats.slice(0, 5);

  /* ------------------------------------------------------------------ */
  /*  Helpers – find node / path                                        */
  /* ------------------------------------------------------------------ */
  function findNodeByValue(
    nodes: FilterOption[],
    val: string
  ): FilterOption | null {
    for (const n of nodes) {
      if (n.value === val) return n;
      if (n.subcategories) {
        const found = findNodeByValue(n.subcategories, val);
        if (found) return found;
      }
    }
    return null;
  }

  function findPathToNode(
    nodes: FilterOption[],
    target: string,
    current: string[] = []
  ): string[] {
    for (const n of nodes) {
      const next = [...current, n.value];
      if (n.value === target) return next;
      if (n.subcategories) {
        const found = findPathToNode(n.subcategories, target, next);
        if (found.length) return found;
      }
    }
    return [];
  }

  return (
    <motion.aside className="hidden md:flex flex-col sticky top-20 bg-white max-h-[calc(100vh-80px)]">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
        {/* Breadcrumb */}
        <motion.nav className="py-3 text-sm text-gray-600 flex items-center mb-4">
          <Link href="/" className="hover:underline">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-1" />
          {searchTerm ? (
            <span>
              Search: {searchTerm} ({productCount})
            </span>
          ) : (
            <Link href="#" className="hover:underline">
              {selectedCategoryData?.name || "All Products"} ({productCount})
            </Link>
          )}
        </motion.nav>

        {/* Filter Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">
            Filters ({selectedFilters.category.length})
          </h2>
          <button
            type="button"
            className="text-blue-800 underline text-sm hover:no-underline"
            onClick={onClearAll}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Scrollable Content Section */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-1">
          <p className="font-medium mb-3">Category</p>

          <div className="space-y-0.5">
            {displayed.map((opt) => (
              <CategoryTreeNode
                key={opt.value}
                node={opt}
                path={[opt.value]}
                selected={selectedFilters.category}
                openPaths={openPaths}
                onPathsChange={onOpenPathsChange}
                onToggle={handleToggle}
                setSlug={setCategorySlug}
              />
            ))}

            {allCats.length > 5 && (
              <div className="pt-3 mt-3 border-t border-gray-200">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setShowAll((v) => !v)}
                  className="text-blue-800"
                >
                  {showAll ? "Show Less" : "Show More"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.aside>
  );
}
